import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, of, tap } from 'rxjs';
import { DataProvider, getWldComponentsInputConfig } from 'pcs-domain';
import { QuickSizerService } from '../../core/services/quick-sizer.service';
import { QuickSizerRequestAdapterService } from '../../core/services/quick-sizer-request-adapter.service';
import { mapQuickSizerResponse } from '../../core/services/quick-sizer-response.mapper';
import { applyComponentSizing, buildQuickSizerCards } from '../../core/services/quick-sizer-component.mapper';
import { DomainInputConfigurationFactoryService } from '../../core/services/wld-input-configuration-factory.service';
import {
  configsFailedError,
  noWorkloadCapacityError,
  parseQuickSizerHttpError,
} from '../../core/services/quick-sizer-error';
import { SpinnerService } from '../../core/services/spinner/spinner.service';
import { ReadyNodeHardwareBounds } from '../../feature-modules/readynode-selector/readynode-selector.component';
import { emptyWorkloadPools } from '../../feature-modules/quick-sizer/recommendation/vm-sizing-headroom';
import {
  DomainComponent,
  DomainComponentState,
  QS_DATAPROVIDER_COMPONENTS_ARCHITECTURE,
  QS_PRODUCT_NAME,
  QS_PRODUCT_VERSION,
  QuickSizerConfigs,
  QuickSizerOutput,
  QuickSizerRequest,
  QuickSizerStepError,
} from '../../feature-modules/quick-sizer/models/quick-sizer.models';
import {
  CalculateQuickSizerRecommendation,
  LoadQuickSizerComponents,
  LoadQuickSizerConfigs,
  LoadQuickSizerHardwareBounds,
  PatchQuickSizerRequest,
  ResetQuickSizer,
  SetQuickSizerComponents,
} from './quick-sizer.actions';

export interface QuickSizerStateModel {
  configs: QuickSizerConfigs | null;
  request: QuickSizerRequest | null;
  output: QuickSizerOutput | null;
  /** `null` until loaded, or when the fetch failed. */
  hardwareBounds: ReadyNodeHardwareBounds | null;
  /** Management domain components. */
  components: DomainComponentState[];
  /** Component selection as it was when `request` was calculated. The PDF reads this, not `components`. */
  requestComponents: DomainComponentState[];
  loading: { configs: boolean; recommendation: boolean };
  stepError: QuickSizerStepError | null;
}

export const QUICK_SIZER_DEFAULTS: QuickSizerStateModel = {
  configs: null,
  request: null,
  output: null,
  hardwareBounds: null,
  components: [],
  requestComponents: [],
  loading: { configs: false, recommendation: false },
  stepError: null,
};

/** Deep-copies `parts` too, so a later toggle cannot change the snapshot. */
function snapshotComponents(components: DomainComponentState[]): DomainComponentState[] {
  return components.map(c => ({ ...c, parts: c.parts.map(part => ({ ...part })) }));
}

function toEnabled(components: DomainComponent[]): DomainComponentState[] {
  return components.map(c => ({ ...c, enabled: c.isRequired || c.enabledByDefault }));
}

/** Quick Sizer wizard state. Nothing here is saved to VCPM. */
@State<QuickSizerStateModel>({
  name: 'quickSizer',
  defaults: QUICK_SIZER_DEFAULTS,
})
@Injectable()
export class QuickSizerState {
  private readonly quickSizer = inject(QuickSizerService);
  private readonly adapter = inject(QuickSizerRequestAdapterService);
  private readonly inputConfigs = inject(DomainInputConfigurationFactoryService);
  private readonly spinner = inject(SpinnerService);

  @Selector()
  static configs(state: QuickSizerStateModel): QuickSizerConfigs | null {
    return state.configs;
  }

  @Selector()
  static request(state: QuickSizerStateModel): QuickSizerRequest | null {
    return state.request;
  }

  @Selector()
  static output(state: QuickSizerStateModel): QuickSizerOutput | null {
    return state.output;
  }

  @Selector()
  static hardwareBounds(state: QuickSizerStateModel): ReadyNodeHardwareBounds | null {
    return state.hardwareBounds;
  }

  @Selector()
  static managementComponents(state: QuickSizerStateModel): DomainComponentState[] {
    return state.components;
  }

  @Selector()
  static requestComponents(state: QuickSizerStateModel): DomainComponentState[] {
    return state.requestComponents;
  }

  @Selector()
  static stepError(state: QuickSizerStateModel): QuickSizerStepError | null {
    return state.stepError;
  }

  @Selector()
  static canCalculate(state: QuickSizerStateModel): boolean {
    return !!state.configs
      && !state.loading.configs
      && !state.loading.recommendation
      && state.stepError?.kind !== 'CONFIGS_FAILED';
  }

  @Action(LoadQuickSizerConfigs)
  loadConfigs(ctx: StateContext<QuickSizerStateModel>) {
    ctx.patchState({
      loading: { ...ctx.getState().loading, configs: true },
      stepError: ctx.getState().stepError?.kind === 'CONFIGS_FAILED' ? null : ctx.getState().stepError,
    });
    return this.quickSizer.getConfigs().pipe(
      tap(configs => {
        ctx.patchState({
          configs,
          loading: { ...ctx.getState().loading, configs: false },
        });
      }),
      catchError(() => {
        ctx.patchState({
          configs: null,
          loading: { ...ctx.getState().loading, configs: false },
          stepError: configsFailedError(),
        });
        return of(null);
      }),
    );
  }

  /** Soft-fails: without the bounds the selector falls back to its default ceilings. */
  @Action(LoadQuickSizerHardwareBounds)
  loadHardwareBounds(ctx: StateContext<QuickSizerStateModel>) {
    return this.quickSizer.getHardwareBounds().pipe(
      tap(hardwareBounds => ctx.patchState({ hardwareBounds })),
      catchError((error: unknown) => {
        console.error('Quick Sizer: hardware bounds failed to load', error);
        ctx.patchState({ hardwareBounds: null });
        return of(null);
      }),
    );
  }

  @Action(LoadQuickSizerComponents)
  loadComponents(ctx: StateContext<QuickSizerStateModel>) {
    return this.quickSizer.getManagementComponents().pipe(
      tap(rows => ctx.patchState({ components: toEnabled(this.buildCards(rows)) })),
      catchError((error: unknown) => {
        console.error('Quick Sizer: VCF components failed to load', error);
        ctx.patchState({ components: [] });
        return of(null);
      }),
    );
  }

  /** Builds the cards from the same input config and adapter the forward sizer uses. */
  private buildCards(rows: DataProvider.Component[]): DomainComponent[] {
    const config = this.inputConfigs.getConfiguration(
      QS_PRODUCT_NAME,
      QS_PRODUCT_VERSION,
      QS_DATAPROVIDER_COMPONENTS_ARCHITECTURE,
      'MANAGEMENT',
    );
    if (!config) {
      throw new Error(`No VCF component configuration for ${QS_PRODUCT_NAME} ${QS_PRODUCT_VERSION} MANAGEMENT`);
    }
    return buildQuickSizerCards(getWldComponentsInputConfig(rows, config.configuration), rows);
  }

  @Action(SetQuickSizerComponents)
  setComponents(ctx: StateContext<QuickSizerStateModel>, { components }: SetQuickSizerComponents) {
    ctx.patchState({ components: components.map(c => ({ ...c })) });
  }

  @Action(PatchQuickSizerRequest)
  patchRequest(ctx: StateContext<QuickSizerStateModel>, { request }: PatchQuickSizerRequest) {
    ctx.patchState({ request: { ...request } });
  }

  @Action(ResetQuickSizer)
  reset(ctx: StateContext<QuickSizerStateModel>) {
    ctx.setState({ ...QUICK_SIZER_DEFAULTS });
  }

  @Action(CalculateQuickSizerRecommendation)
  calculate(ctx: StateContext<QuickSizerStateModel>, { request }: CalculateQuickSizerRecommendation) {
    const state = ctx.getState();
    const requestComponents = snapshotComponents(state.components);
    if (!state.configs) {
      ctx.patchState({
        request: { ...request },
        requestComponents,
        stepError: configsFailedError(),
        output: null,
      });
      return of(null);
    }
    // Clear the previous result so a failed retry cannot show numbers for the old inputs.
    ctx.patchState({
      request: { ...request },
      requestComponents,
      loading: { ...state.loading, recommendation: true },
      stepError: null,
      output: null,
    });
    this.spinner.show();
    const body = this.adapter.toApiRequest(request, state.components);
    return this.quickSizer.getRecommendation(body).pipe(
      tap(response => {
        const output = mapQuickSizerResponse(response);
        // A pool at 0 means every VM count is 0, so treat it as an input error.
        const emptyPools = emptyWorkloadPools(output);
        const stepError = emptyPools.length ? noWorkloadCapacityError(emptyPools) : null;
        ctx.patchState({
          output: stepError ? null : output,
          requestComponents: applyComponentSizing(ctx.getState().requestComponents, output.componentSizing),
          stepError,
          loading: { ...ctx.getState().loading, recommendation: false },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        ctx.patchState({
          stepError: parseQuickSizerHttpError(error),
          output: null,
          loading: { ...ctx.getState().loading, recommendation: false },
        });
        return of(null);
      }),
      finalize(() => this.spinner.hide()),
    );
  }
}
